import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { AR_TASK_NAMESPACE_KEY } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import useAuth from '@/api/auth/useAuth';
import type { TaskResponse, TaskStatus } from '@erp/rpc';

/**
 * Statuses that count as "needs attention" on the AR summary dashboard.
 * Terminal states (completed/failed/cancelled) are excluded — the dashboard is
 * a work queue, not a history view (that lives in the main inbox).
 */
export const OPEN_TASK_STATUSES: TaskStatus[] = [
  'pending',
  'in_progress',
  'needs_input',
];

const AR_TASKS_LIMIT = 50;

const PRIORITY_WEIGHT: Record<TaskResponse['priority'], number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/**
 * Open tasks in the organization's Accounts Receivable task namespace — the
 * AR-relevant subset of the main task inbox. Backend visibility scoping still
 * applies (non-admins only see tasks they're assignee/creator/watcher of).
 *
 * Resolves the AR namespace by its well-known key first; when the namespace
 * doesn't exist yet (no AR workflow has filed a task), the list query is
 * skipped entirely rather than accidentally listing all tasks unfiltered.
 *
 * Tasks are returned pre-ordered for the dashboard work queue: the current
 * user's tasks first, then unassigned ones, then the rest — each tier sorted
 * by priority, then most recently updated.
 */
export function useArTasks() {
  const { currentUser } = useAuth();
  const [canReadTasks] = useCan(Action.Read, Subject.Task);

  const namespacesQuery = useQuery({
    ...orpc.namespaces.list.queryOptions({ input: {} }),
    enabled: canReadTasks,
    staleTime: 5 * 60 * 1000,
  });
  const arNamespace = useMemo(
    () =>
      (namespacesQuery.data ?? []).find(
        (n) => n.key === AR_TASK_NAMESPACE_KEY,
      ) ?? null,
    [namespacesQuery.data],
  );

  const tasksQuery = useQuery({
    ...orpc.tasks.list.queryOptions({
      input: {
        taskNamespaceId: arNamespace?.id ?? '',
        statuses: OPEN_TASK_STATUSES,
        limit: AR_TASKS_LIMIT,
        offset: 0,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      },
    }),
    enabled: canReadTasks && arNamespace != null,
    refetchInterval: 30_000,
  });

  const tasks: TaskResponse[] = useMemo(() => {
    if (!arNamespace) return [];
    const tier = (task: TaskResponse): number => {
      if (task.assigneeUserId === currentUser?.id) return 0;
      if (task.assigneeUserId == null && task.assigneeAgentDefinitionId == null)
        return 1;
      return 2;
    };
    return [...(tasksQuery.data?.tasks ?? [])].sort((a, b) => {
      const tierDiff = tier(a) - tier(b);
      if (tierDiff !== 0) return tierDiff;
      const priorityDiff =
        PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [arNamespace, tasksQuery.data, currentUser?.id]);

  return {
    tasks,
    canReadTasks,
    isLoading:
      canReadTasks &&
      (namespacesQuery.isLoading ||
        (arNamespace != null && tasksQuery.isLoading)),
  };
}
